import { useQueryClient } from "@tanstack/react-query";
import { Mail, MailOpen, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/lib/hooks";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "";
  }
};

export default function Messages() {
  const queryClient = useQueryClient();
  const { data: messages = [], isLoading } = useContent("/admin/messages");

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["/admin/messages"] });
    queryClient.invalidateQueries({ queryKey: ["/admin/stats"] });
  };

  const markRead = async (msg) => {
    await api.put(`/admin/messages/${msg.id}/read`);
    refresh();
  };

  const remove = async (msg) => {
    if (!window.confirm("Bu mesaj silinsin mi?")) return;
    await api.delete(`/admin/messages/${msg.id}`);
    toast.success("Mesaj silindi");
    refresh();
  };

  return (
    <div data-testid="admin-messages">
      <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-950">Mesajlar</h1>
      <p className="mt-1 text-sm text-zinc-500">İletişim formundan gelen mesajlar.</p>

      <div className="mt-6 space-y-4">
        {isLoading ? (
          <p className="text-sm text-zinc-500">Yükleniyor...</p>
        ) : messages.length === 0 ? (
          <p className="rounded-2xl border border-zinc-200 bg-white p-6 text-sm text-zinc-500" data-testid="messages-empty">
            Henüz mesaj yok.
          </p>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              data-testid={`message-${msg.id}`}
              className={`rounded-2xl border bg-white p-6 ${msg.read ? "border-zinc-200" : "border-[#1A4BFF]"}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    {!msg.read && <span className="h-2 w-2 rounded-full bg-[#1A4BFF]" />}
                    <p className="font-bold text-zinc-900">{msg.name}</p>
                    <span className="text-xs text-zinc-400">{formatDate(msg.created_at)}</span>
                  </div>
                  <p className="mt-1 text-sm text-zinc-500">
                    {msg.email}
                    {msg.phone && ` • ${msg.phone}`}
                    {msg.subject && ` • ${msg.subject}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  {!msg.read && (
                    <button
                      data-testid={`message-read-${msg.id}`}
                      onClick={() => markRead(msg)}
                      title="Okundu işaretle"
                      className="rounded-lg border border-zinc-200 p-2 text-zinc-600 transition-colors hover:border-[#1A4BFF] hover:text-[#1A4BFF]"
                    >
                      <MailOpen className="h-4 w-4" />
                    </button>
                  )}
                  {msg.read && <Mail className="h-4 w-4 self-center text-zinc-300" />}
                  <button
                    data-testid={`message-delete-${msg.id}`}
                    onClick={() => remove(msg)}
                    className="rounded-lg border border-zinc-200 p-2 text-zinc-600 transition-colors hover:border-red-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="mt-4 whitespace-pre-wrap rounded-xl bg-zinc-50 p-4 text-sm leading-relaxed text-zinc-700">
                {msg.message}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
